import React, { useEffect, useState } from 'react';

import { FileRejection } from '@mantine/dropzone';
import { useDispatch, useSelector } from 'react-redux';

import { uploadFilesToDropbox } from '../../../slices/DropboxSlice';
import { supportReply } from '../../../slices/LeoSlice';
import { showErrorToast } from '../../../slices/ToastNotificationSlice';
import { AppDispatch, MimeType, RootState } from '../../../types';
import { UserIntentEnum } from '../../../utils/LeoUtils';
import Button from '../../Button';
import DragAndDropInput from '../../DragAndDropInput';

const context = {
  file_allowed_mime_types: [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/jpg',
  ] as MimeType[],
  file_max_size: 20,
  file_max_count: 10,
};

interface NeoLeoShareFilesDnDProps {
  session_id: string;
}

const NeoLeoShareFilesDnD: React.FC<NeoLeoShareFilesDnDProps> = ({
  session_id,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const authUserDetail = useSelector(
    (state: RootState) => state.auth.authUserDetail,
  );

  const [files, setFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [error, setError] = useState<string>('');

  const handleCreateTicket = async (attachmentIds: string[]) => {
    await dispatch(
      supportReply(UserIntentEnum.SUPPORT_SELECTED, session_id, attachmentIds),
    );
  };

  const handleDrop = (files: File[]) => {
    setFiles(files);
  };

  const handleRemoveFile = (file: File) => {
    setFiles(files.filter((f) => f !== file));
  };

  const uploadFiles = async () => {
    setIsUploadingFiles(true);
    const filesResponse = await dispatch(
      uploadFilesToDropbox(
        authUserDetail?.id!,
        authUserDetail!.dropboxId!,
        files,
      ),
    );
    const fileIds = filesResponse?.map((file) => file.id!);
    handleCreateTicket(fileIds!);
    setIsUploadingFiles(false);
  };

  const onSubmit = async () => {
    await uploadFiles();
  };

  const handleError = (rejections: FileRejection[]) => {
    setError(rejections[0].errors[0].message);
  };

  useEffect(() => {
    if (!!error) {
      dispatch(showErrorToast(error, '', 5000));
    }
  }, [dispatch, error]);

  return (
    <div className='font-inter flex flex-col gap-3'>
      <p className='text-white font-normal'>
        Share any image or files related to your issue.
      </p>
      <DragAndDropInput
        data-testid='file-input'
        accept={context?.file_allowed_mime_types}
        theme='NAVY'
        attachedFilesClassName='!p-0 !bg-transparent'
        onDrop={handleDrop}
        onRemoveFile={handleRemoveFile}
        maxSize={context?.file_max_size}
        maxFiles={context?.file_max_count}
        multiple={(context?.file_max_count ?? 0) > 1}
        files={files}
        onReject={handleError}
        showAttachedFiles
        noBorder
      />
      <div className='flex gap-2'>
        <Button
          type='button'
          variant='primary-outline'
          fullWidth
          label='No, thanks'
          onPress={() => handleCreateTicket([])}
        />
        {files.length > 0 && (
          <Button
            type='submit'
            variant='primary'
            fullWidth
            label='Submit'
            onPress={onSubmit}
            isDisabled={isUploadingFiles}
            isSubmitting={isUploadingFiles}
          />
        )}
      </div>
    </div>
  );
};

export default NeoLeoShareFilesDnD;
